import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  getUsers,
  getUser,
  updateUser,
  deleteUser,
  activateUser,
  deactivateUser,
  getUserStats,
  getSalesReps,
  inviteUser,
} from '@/services/users';
import type { UserRole, UserStatus, Business } from '@/types';

// ===========================================
// Query Keys
// ===========================================

export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...userKeys.lists(), filters] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (id: string) => [...userKeys.details(), id] as const,
  stats: () => [...userKeys.all, 'stats'] as const,
  salesReps: () => [...userKeys.all, 'salesReps'] as const,
};

// ===========================================
// User Hooks
// ===========================================

/**
 * Fetch all users
 */
export function useUsers() {
  return useQuery({
    queryKey: userKeys.lists(),
    queryFn: getUsers,
  });
}

/**
 * Fetch a single user
 */
export function useUser(id: string | undefined) {
  return useQuery({
    queryKey: userKeys.detail(id!),
    queryFn: () => getUser(id!),
    enabled: !!id,
  });
}

/**
 * Fetch user statistics
 */
export function useUserStats() {
  return useQuery({
    queryKey: userKeys.stats(),
    queryFn: getUserStats,
  });
}

/**
 * Fetch active sales reps
 */
export function useSalesReps() {
  return useQuery({
    queryKey: userKeys.salesReps(),
    queryFn: getSalesReps,
  });
}

/**
 * Update a user
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<{
        displayName: string;
        role: UserRole;
        status: UserStatus;
        mondayPersonId: string | null;
        preferredBusiness: Business;
      }>;
    }) => updateUser(id, updates),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: userKeys.stats() });
      toast.success('User updated successfully');
    },
    onError: (error) => {
      console.error('Update user error:', error);
      toast.error('Failed to update user');
    },
  });
}

/**
 * Activate a user
 */
export function useActivateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: activateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.stats() });
      queryClient.invalidateQueries({ queryKey: userKeys.salesReps() });
      toast.success('User activated');
    },
    onError: (error) => {
      console.error('Activate user error:', error);
      toast.error('Failed to activate user');
    },
  });
}

/**
 * Deactivate a user
 */
export function useDeactivateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deactivateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.stats() });
      queryClient.invalidateQueries({ queryKey: userKeys.salesReps() });
      toast.success('User deactivated');
    },
    onError: (error) => {
      console.error('Deactivate user error:', error);
      toast.error('Failed to deactivate user');
    },
  });
}

/**
 * Delete a user
 */
export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.stats() });
      toast.success('User deleted');
    },
    onError: (error) => {
      console.error('Delete user error:', error);
      toast.error('Failed to delete user');
    },
  });
}

/**
 * Invite a new user via email
 */
export function useInviteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: inviteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.invalidateQueries({ queryKey: userKeys.stats() });
      toast.success('Invitation sent successfully');
    },
    onError: (error: any) => {
      console.error('Invite user error:', error);
      const message = error?.message || 'Failed to send invitation';
      toast.error(message);
    },
  });
}
